import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { AuthContext } from "context";
import { useWebSocket } from "context/WebSocketContext";
import api, { installationMessage } from "services/installation-api";
import { resolveGeometry } from "./capacity";
import { resolveTankSizing } from "./tankSizing";
import { calibrationError, calibrationPayload, emptyCalibration } from "./calibration";
import useCalibrationSave from "./useCalibrationSave";
import {
  STEPS,
  SECTIONS,
  STEP_SECTION,
  deriveSteps,
  initialState,
  reducer,
  loadDraft,
  saveDraft,
  clearDraft,
  contactKey,
  emptyCustomerForm,
  emptyAddressForm,
  emptyAssetForm,
} from "./installState";
import {
  buildCustomerPayload,
  buildAddressPayload,
  buildAssetPayload,
  buildContactPayload,
  submittedAttributes,
  tankAttributeMap,
} from "./payloads";
import { buildThresholdRows, missingRequiredThresholds, submittedThresholdRows } from "../claim/thresholds";
import { draftCustomerIdentity, entityDisplayName, newCustomerDefaults } from "../claim/customerIdentity";
import { CLAIM_STAGES, claimStageCopy, outcomeStage, unavailableReason } from "../claim/claimTitles";
import ClaimHead from "../claim/ClaimHead";
import InstallationSummary from "../claim/InstallationSummary";
import { destinationKey, selectedRecipients } from "../claim/AlertSettings";
import {
  applyFollowUps,
  completeReviewedInstallation,
  readProgress,
  writeProgress,
  supportsCalibrationOperation,
} from "../claim/finalizeInstallation";
import { useInstallTheme, WizardShell, PrimaryButton, SecondaryButton, ErrorList, StepAlert } from "./ui";
import { CustomerModeStep, CustomerSearchStep, CustomerCreateStep } from "./steps/CustomerSteps";
import {
  AddressModeStep,
  AddressSelectStep,
  AddressCreateStep,
  AssetModeStep,
  AssetSelectStep,
  AssetCreateStep,
} from "./steps/SiteSteps";
import {
  ContactsStep,
  DeviceDetailsStep,
  ThresholdsStep,
  NotificationsStep,
  ProviderSharingStep,
} from "./steps/DetailSteps";
import { ClaimGate, InstallationReviewStep } from "./steps/ReviewSteps";
import CalibrationStep from "./steps/CalibrationStep";

const STEP_STAGE = {
  [STEPS.CUSTOMER_MODE]: CLAIM_STAGES.CUSTOMER,
  [STEPS.CUSTOMER_SEARCH]: CLAIM_STAGES.CUSTOMER,
  [STEPS.CUSTOMER_NEW]: CLAIM_STAGES.CUSTOMER,
  [STEPS.ADDRESS_MODE]: CLAIM_STAGES.ADDRESS,
  [STEPS.ADDRESS_SELECT]: CLAIM_STAGES.ADDRESS,
  [STEPS.ADDRESS_NEW]: CLAIM_STAGES.ADDRESS,
  [STEPS.CONTACTS]: CLAIM_STAGES.CONTACTS,
  [STEPS.ASSET_MODE]: CLAIM_STAGES.ASSET_DEVICE,
  [STEPS.ASSET_SELECT]: CLAIM_STAGES.ASSET_DEVICE,
  [STEPS.ASSET_NEW]: CLAIM_STAGES.ASSET_DEVICE,
  [STEPS.DEVICE]: CLAIM_STAGES.ASSET_DEVICE,
  [STEPS.CALIBRATION]: CLAIM_STAGES.ASSET_DEVICE,
  [STEPS.THRESHOLDS]: CLAIM_STAGES.ALERTS,
  [STEPS.NOTIFICATIONS]: CLAIM_STAGES.ALERTS,
  [STEPS.PROVIDERS]: CLAIM_STAGES.PROVIDERS,
  [STEPS.REVIEW]: CLAIM_STAGES.REVIEW,
};

const STEP_META = {
  [STEPS.CUSTOMER_MODE]: { title: "Who is this installation for?" },
  [STEPS.CUSTOMER_SEARCH]: { title: "Find the customer" },
  [STEPS.CUSTOMER_NEW]: { title: "New customer" },
  [STEPS.ADDRESS_MODE]: { title: "Which service address?" },
  [STEPS.ADDRESS_SELECT]: { title: "Choose a service address" },
  [STEPS.ADDRESS_NEW]: { title: "Add service address" },
  [STEPS.ASSET_MODE]: { title: "Is the tank or asset already in Rivio?" },
  [STEPS.ASSET_SELECT]: { title: "Choose the asset" },
  [STEPS.ASSET_NEW]: { title: "Add the tank or asset" },
  [STEPS.CONTACTS]: { title: "Contacts and who can access this device" },
  [STEPS.DEVICE]: { title: "Set up the monitor" },
  [STEPS.CALIBRATION]: { title: "Calibrate the level sensor" },
  [STEPS.THRESHOLDS]: { title: "Set alert levels" },
  [STEPS.NOTIFICATIONS]: { title: "Who gets alerts?" },
  [STEPS.PROVIDERS]: { title: "Share this asset with a provider" },
  [STEPS.REVIEW]: { title: "Confirm the complete installation record" },
};

function sessionKey(claimCode) {
  return `rivio_device_installation:${claimCode}`;
}

function readSession(claimCode) {
  try {
    const raw = sessionStorage.getItem(sessionKey(claimCode));
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (!value.session_id || typeof value.session_token !== "string" || !value.claim_id) return null;
    return value;
  } catch {
    return null;
  }
}

function entityIdOf(entity) {
  return Number(entity?.attributes?.entity_id ?? entity?.entity_id ?? 0) || null;
}

function addressIdOf(address) {
  const row = address?.attributes || address || {};
  return row.address_id ?? row.Address?.address_id ?? row.id ?? null;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function contactTypeLabel(dropdowns, id) {
  return (dropdowns.contactTypes || []).find((row) => String(row.contact_type_id) === String(id))?.type_name || "";
}

export default function InstallFlow({ claimCode }) {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const { t, darkMode } = useInstallTheme();

  const [state, dispatch] = useReducer(reducer, claimCode, (code) => {
    const draft = loadDraft(code);
    return draft ? { ...initialState(code), ...draft } : initialState(code);
  });
  const calibrationSave = useCalibrationSave(claimCode);
  // Device type comes from the installation overview only - never from a
  // model name, tank name or external device ID.
  const isLevelDevice = Number(state.deviceTypeId) === 4;
  // Shared app socket, used only for optional device-test hints. Never
  // disconnected here; a missing socket just means polling.
  const { socket } = useWebSocket() || {};
  const [routing, setRouting] = useState(null);
  const [routingError, setRoutingError] = useState("");
  const [session, setSession] = useState(() => readSession(claimCode));
  const [overview, setOverview] = useState(null);
  const [dropdowns, setDropdowns] = useState({});
  const [dropdownsLoaded, setDropdownsLoaded] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [targets, setTargets] = useState(null);
  const [profile, setProfile] = useState(null);
  const [termsVersion, setTermsVersion] = useState(null);
  // Server-advertised setup capabilities (session-level setup options).
  // level_calibration_operation switches calibration to the claim-scoped
  // operation; installation_device_test enables the optional device test.
  const [setupCapabilities, setSetupCapabilities] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [assignmentError, setAssignmentError] = useState("");
  const [summary, setSummary] = useState(null);
  const [operations, setOperations] = useState(null);
  const [followUpError, setFollowUpError] = useState("");
  const submitLock = useRef(false);
  const steps = deriveSteps(state);
  const stepKey = steps[state.stepIndex] || steps[0];
  const meta = STEP_META[stepKey] || { title: "" };
  const deviceId = overview?.device?.device_id ?? state.result?.device_id ?? summary?.snapshot?.device_id ?? null;
  const calibrationViaOperation = supportsCalibrationOperation({ capabilities: setupCapabilities });
  const deviceTestAvailable = setupCapabilities?.installation_device_test === true;

  // ---- bootstrap ----
  useEffect(() => {
    let active = true;
    api.routing(claimCode).then(
      (r) => active && setRouting(r),
      (e) => {
        if (!active) return;
        setRoutingError(installationMessage(e));
        setErrors([installationMessage(e)]);
      },
    );
    api.dropdowns()
      .then((d) => active && setDropdowns(d || {}))
      .catch(() => {})
      .finally(() => active && setDropdownsLoaded(true));
    return () => {
      active = false;
    };
  }, [claimCode]);

  // A completed claim is shown from the server's historical summary.
  const loadSummary = useCallback(async (claimId) => {
    const data = await api.summary(claimId);
    setSummary(data);
    setOperations(data?.operations || []);
    return data;
  }, []);

  useEffect(() => {
    const stored = readProgress(claimCode);
    if (!stored?.claimId || session) return;
    loadSummary(stored.claimId).catch(() => {});
  }, [claimCode, session, loadSummary]);

  // Recover a completed installation that still has a pending local
  // calibration request (closed/reloaded tab). It verifies claim status and
  // identity but deliberately never issues the PUT on its own: a stale local
  // request must not silently overwrite a configuration changed elsewhere.
  useEffect(() => {
    const pending = calibrationSave.job;
    if (!auth.isAuthenticated || !pending || state.result || summary) return undefined;
    let active = true;
    (async () => {
      try {
        const status = await api.status(pending.claimId);
        if (!active || status.status !== "completed") return;
        if (Number(status.device_id) !== pending.deviceId ||
            Number(status.installed_address_item_id) !== pending.assetId) {
          throw new Error("The saved calibration does not match the completed installation.");
        }
        dispatch({ type: "SET_RESULT", result: status });
        writeProgress(claimCode, { claimId: pending.claimId });
        try {
          await loadSummary(pending.claimId);
        } catch (problem) {
          if (active) setErrors([installationMessage(problem)]);
        }
      } catch (problem) {
        if (active) setErrors([installationMessage(problem)]);
      }
    })();
    return () => { active = false; };
  }, [auth.isAuthenticated, calibrationSave.job, state.result, summary, claimCode, loadSummary]);

  useEffect(() => {
    if (!session) return undefined;
    let active = true;
    (async () => {
      try {
        const detail = await api.overview(session);
        if (!active) return;
        setOverview(detail);
        const typeId = Number(detail?.device?.device_type_id);
        if (!Number.isSafeInteger(typeId) || typeId <= 0) {
          throw new Error("The installation overview did not return a valid device type.");
        }
        dispatch({ type: "HYDRATE", payload: { deviceTypeId: typeId } });
        const metaLookups = await api.loadThresholdLookups().catch(() => null);
        if (!active) return;
        // Draft edits survive reload: the restored rows are the "draft" source.
        const thresholdRows = buildThresholdRows({
          definitions: detail.threshold_definitions || [],
          existing: detail.thresholds || [],
          draft: state.thresholds,
          lookups: metaLookups,
        });
        dispatch({ type: "SET_THRESHOLDS", thresholds: thresholdRows });
        if (!state.device.device_name && detail.device?.name) {
          dispatch({ type: "SET_DEVICE", device: { device_name: detail.device.name } });
        }
        const base = await api.claimSetupOptions(session).catch(() => null);
        if (active) {
          setTermsVersion(base?.lookups?.terms_version || null);
          setSetupCapabilities(base?.capabilities || null);
        }
      } catch (e) {
        if (active) setErrors([installationMessage(e)]);
      }
    })();
    return () => {
      active = false;
    };
  }, [session?.session_id]);

  // ---- draft persistence (never stores tokens or attestations as accepted) ----
  useEffect(() => {
    if (state.stepIndex === 0 && !state.branch.customerMode) return;
    saveDraft(state);
  }, [state]);

  // ---- new customer defaults: Active status, Rivio company (by name) ----
  useEffect(() => {
    if (state.branch.customerMode !== "new" || !dropdownsLoaded) return;
    const form = state.customerForm || emptyCustomerForm();
    if (form.status_type_id && form.company_id) return;
    const defaults = newCustomerDefaults(dropdowns);
    const patch = {};
    if (!form.status_type_id && defaults.status_type_id) patch.status_type_id = String(defaults.status_type_id);
    if (!form.company_id && defaults.company_id) patch.company_id = String(defaults.company_id);
    if (Object.keys(patch).length) dispatch({ type: "SET_CUSTOMER_FORM", form: patch });
  }, [state.branch.customerMode, dropdownsLoaded, dropdowns, state.customerForm]);

  // ---- load per-customer context (scoped profile, addresses, assets) ----
  const loadProfile = useCallback(async (activeSession, entityId) => {
    setProfile({ loading: true });
    try {
      const data = await api.claimSetupOptions(activeSession, { entityId });
      setProfile(data);
      return data;
    } catch (e) {
      setProfile({ error: installationMessage(e) });
      return null;
    }
  }, []);

  useEffect(() => {
    const entityId = entityIdOf(state.customer);
    if (!session || !entityId) {
      setAddresses([]);
      setTargets(null);
      setProfile(null);
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        const [targetRows, addressRows] = await Promise.all([
          api.targets(session, entityId),
          api.listAddresses(entityId).catch(() => []),
          loadProfile(session, entityId),
        ]);
        if (!active) return;
        setTargets(targetRows?.assets ?? targetRows ?? []);
        setAddresses(addressRows || []);
      } catch (e) {
        if (active) setErrors([installationMessage(e)]);
      }
    })();
    return () => {
      active = false;
    };
  }, [session, state.customer, loadProfile]);

  useEffect(() => {
    if (!session || overview?.device?.device_id == null) return;
    let active = true;
    api.shareOptions().then((rows) => active && dispatch({ type: "SET_PROVIDER_OPTIONS", options: rows })).catch(() => {});
    return () => {
      active = false;
    };
  }, [session, overview?.device?.device_id]);

  useEffect(() => {
    let active = true;
    if (!auth.isAuthenticated) return undefined;
    setCustomersLoading(true);
    api.searchCustomers()
      .then((rows) => active && setCustomers(rows))
      .catch(() => {})
      .finally(() => active && setCustomersLoading(false));
    return () => {
      active = false;
    };
  }, [auth.isAuthenticated]);

  const startOrResume = useCallback(async () => {
    if (submitLock.current) return;
    submitLock.current = true;
    setBusy(true);
    setErrors([]);
    try {
      const existing = session || readSession(claimCode);
      if (existing) {
        const status = await api.status(existing.claim_id).catch(() => null);
        if (status?.status === "completed") {
          sessionStorage.removeItem(sessionKey(claimCode));
          clearDraft(claimCode);
          writeProgress(claimCode, { claimId: existing.claim_id });
          dispatch({ type: "SET_RESULT", result: status });
          await loadSummary(existing.claim_id).catch(() => {});
          return;
        }
        setSession(existing);
        return;
      }
      const next = await api.start(claimCode);
      sessionStorage.setItem(sessionKey(claimCode), JSON.stringify(next));
      setSession(next);
    } catch (e) {
      setErrors([installationMessage(e)]);
    } finally {
      submitLock.current = false;
      setBusy(false);
    }
  }, [claimCode, session, loadSummary]);

  // ---- derived review data ----
  const lookups = profile?.lookups || null;
  const selectedAssetId = state.progress.asset || state.asset?.address_item_id || null;

  const accessRows = useMemo(() => {
    const rows = [];
    const existingAccess = new Map(
      (profile?.contact_access || [])
        .filter((row) => selectedAssetId && Number(row.address_item_id) === Number(selectedAssetId))
        .map((row) => [Number(row.contact_id), row]),
    );
    if (state.branch.customerMode === "existing") {
      (profile?.recipients || [])
        .filter((row) => row.scope === "entity")
        .forEach((row) => {
          const key = `e${row.contact_id}`;
          const current = existingAccess.get(Number(row.contact_id));
          const chosen = state.contactAccess?.[key];
          rows.push({
            key,
            name: row.contact_name,
            contact_id: row.contact_id,
            type: row.contact_type,
            enabled: chosen?.enabled ?? Boolean(current),
            access_type: chosen?.access_type || current?.access_type || "read",
            service_access_type: chosen?.service_access_type || current?.service_access_type || "read",
          });
        });
    }
    state.contacts.forEach((contact, index) => {
      if (!contact.first_name?.trim() && !contact.email?.trim()) return;
      const key = contactKey(contact, index);
      const chosen = state.contactAccess?.[key];
      const created = state.progress.contacts?.[key];
      rows.push({
        key,
        name: `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || contact.email,
        contact_id: typeof created === "number" ? created : null,
        client_ref: key,
        email: contact.email?.trim() || null,
        phone: contact.phone?.trim() || null,
        type: contactTypeLabel(dropdowns, contact.contact_type_id),
        enabled: Boolean(chosen?.enabled),
        access_type: chosen?.access_type || "read",
        service_access_type: chosen?.service_access_type || "read",
      });
    });
    return rows;
  }, [profile, state.contacts, state.contactAccess, state.branch.customerMode, state.progress.contacts, selectedAssetId, dropdowns]);

  const destinations = useMemo(() => {
    const rows = [];
    const types = lookups?.address_types || {};
    if (state.branch.customerMode === "existing") {
      (profile?.recipients || [])
        .filter((row) => row.scope === "entity")
        .forEach((row) => {
          (row.addresses || []).forEach((address) => {
            const kind = Number(address.address_type_id) === Number(types.email)
              ? "email"
              : Number(address.address_type_id) === Number(types.phone) ? "sms" : null;
            if (!kind || !address.value) return;
            const entry = { scope: "entity", contact_id: row.contact_id, kind, value: address.value, label: row.contact_name };
            rows.push({ ...entry, key: destinationKey(entry) });
          });
        });
    }
    state.contacts.forEach((contact, index) => {
      const key = contactKey(contact, index);
      const name = `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || "New contact";
      [["email", contact.email], ["sms", contact.phone]].forEach(([kind, value]) => {
        if (!value?.trim()) return;
        const entry = { scope: "entity", client_ref: key, kind, value: value.trim(), label: name };
        rows.push({ ...entry, key: destinationKey(entry) });
      });
    });
    return rows;
  }, [profile, lookups, state.contacts, state.branch.customerMode]);

  const recipients = useMemo(() => selectedRecipients(state.alerts, destinations), [state.alerts, destinations]);

  const customerIdentity = useMemo(() => {
    if (state.branch.customerMode === "new") {
      const lookupsForIdentity = {
        entityTypes: dropdowns.entityTypes,
        companies: dropdowns.companies || dropdowns.company,
        entityStatusOptions: dropdowns.statusOptions || dropdowns.statusTypes,
      };
      return { ...draftCustomerIdentity(state.customerForm || {}, lookupsForIdentity, { lookupsLoaded: dropdownsLoaded }), id: state.progress.customer };
    }
    const id = entityIdOf(state.customer);
    if (!id) return { name: "Required - missing", entityType: "-", company: "-", status: "-", complete: false, missing: ["Customer"] };
    if (!profile || profile.loading) {
      return { name: entityDisplayName(state.customer), entityType: "Loading…", company: "Loading…", status: "Loading…", complete: false, loading: true, id };
    }
    if (profile.error) {
      return { name: entityDisplayName(state.customer), entityType: "Unavailable", company: "Unavailable", status: "Unavailable", complete: false, error: profile.error, id };
    }
    const c = profile.customer || {};
    return {
      id,
      name: c.display_name || entityDisplayName(state.customer),
      entityType: c.entity_type || `ID ${c.entity_type_id ?? "-"}`,
      company: c.company || `ID ${c.company_id ?? "-"}`,
      status: c.status || `ID ${c.status_type_id ?? "-"}`,
      complete: true,
    };
  }, [state.branch.customerMode, state.customerForm, state.customer, state.progress.customer, profile, dropdowns, dropdownsLoaded]);

  // ---- validation gates ----
  const gateError = useMemo(() => {
    switch (stepKey) {
      case STEPS.CUSTOMER_SEARCH:
        return state.customer ? "" : "Select a customer to continue.";
      case STEPS.CUSTOMER_NEW: {
        const f = state.customerForm || emptyCustomerForm();
        if (!f.entity_type_id || !f.company_id || !f.status_type_id) return "Customer type, company, and status are required.";
        if (!customerIdentity.complete) return "Customer name is required.";
        return "";
      }
      case STEPS.ADDRESS_SELECT:
        return state.address ? "" : "Select a service address.";
      case STEPS.ADDRESS_NEW: {
        const f = state.addressForm || emptyAddressForm();
        if (!f.street_address1.trim() || !f.city.trim()) return "Street and city are required.";
        return "";
      }
      case STEPS.ASSET_SELECT:
        return state.asset ? "" : "Select an asset.";
      case STEPS.ASSET_NEW: {
        const f = state.assetForm || emptyAssetForm();
        if (!f.name.trim() || !f.item_id) return "Asset name and type are required.";
        return "";
      }
      case STEPS.DEVICE:
        if (!state.device.device_name.trim() || !state.device.map_identifier.trim())
          return "Device name and asset identifier are required.";
        if (!state.device.installation_date) return "Installation date is required.";
        if (state.device.installation_date > todayIso()) return "Installation date cannot be in the future.";
        return "";
      case STEPS.CALIBRATION:
        return calibrationError(state.calibration);
      case STEPS.THRESHOLDS: {
        const missing = missingRequiredThresholds(state.thresholds);
        return missing.length ? `Enter required thresholds: ${missing.map((r) => r.parameter_label).join(", ")}.` : "";
      }
      case STEPS.NOTIFICATIONS:
        return state.alerts?.enabled && !recipients.length ? "Choose at least one recipient, or turn alerts off." : "";
      default:
        return "";
    }
  }, [stepKey, state, customerIdentity, recipients]);

  const reviewBlockers = useMemo(() => {
    const list = [];
    if (!customerIdentity.complete) {
      if (customerIdentity.loading) list.push("Customer details are still loading.");
      else if (customerIdentity.error) list.push(`Customer details could not be loaded (${customerIdentity.error}). Go to Customer to retry.`);
      else list.push(`Customer is incomplete (${(customerIdentity.missing || []).join(", ")} missing). Go to Customer to fix it.`);
    }
    if (customerIdentity.unresolved?.length) list.push(`Customer ${customerIdentity.unresolved.join(", ")} could not be resolved. Go to Customer and choose it again.`);
    const missing = missingRequiredThresholds(state.thresholds);
    if (missing.length) list.push(`Required thresholds are missing: ${missing.map((r) => r.parameter_label).join(", ")}.`);
    if (!state.device.device_name.trim() || !state.device.installation_date) list.push("Device name and installation date are required.");
    if (state.device.installation_date > todayIso()) list.push("Installation date cannot be in the future.");
    if (!termsVersion) list.push("Installation terms could not be loaded. Reload the page before confirming.");
    if (state.alerts?.enabled && !recipients.length) list.push("Alerts are on but no recipients are selected.");
    if (!state.deviceTypeId) list.push("Device details are still loading. Reload if this does not finish.");
    if (isLevelDevice) {
      const problem = calibrationError(state.calibration);
      if (problem) list.push(problem);
    }
    if (calibrationSave.error && !calibrationSave.job) {
      list.push(calibrationSave.error);
    }
    if (state.tankGeometry) {
      const checked = resolveTankSizing(state.tankSizing);
      if (!checked.geometry ||
          JSON.stringify(checked.geometry) !== JSON.stringify(state.tankGeometry)) {
        list.push("Tank size inputs changed or are invalid. Review and accept the helper result again.");
      }
    }
    return list;
  }, [
    customerIdentity,
    state.thresholds,
    state.device,
    state.deviceTypeId,
    state.calibration,
    state.tankSizing,
    state.tankGeometry,
    isLevelDevice,
    calibrationSave.error,
    calibrationSave.job,
    termsVersion,
    state.alerts,
    recipients,
  ]);

  // ---- follow-ups (claim-scoped, after completion only) ----
  const runFollowUps = useCallback(async (claimId, plan) => {
    const result = await applyFollowUps({ api, claimId, operations: plan });
    if (result.summary) setSummary(result.summary);
    setOperations(result.operations);
    setFollowUpError(result.error ? installationMessage(result.error) : "");
    return result;
  }, []);

  const retryFollowUps = useCallback(async (failed) => {
    const claimId = summary?.snapshot?.claim_id || state.progress.claimId || readProgress(claimCode)?.claimId;
    if (!claimId) return;
    setBusy(true);
    try {
      await runFollowUps(claimId, failed);
    } finally {
      setBusy(false);
    }
  }, [summary, state.progress.claimId, claimCode, runFollowUps]);

  const submit = useCallback(async () => {
    if (submitLock.current || !session) return;
    submitLock.current = true;
    setBusy(true);
    setSubmitting(true);
    setErrors([]);
    setAssignmentError("");
    const progress = { ...state.progress, contacts: { ...(state.progress.contacts || {}) } };
    try {
      // Revalidate everything the review screen relied on before any record is
      // created: device type, calibration measurements and accepted geometry.
      if (!state.deviceTypeId) throw new Error("Device details must load before confirming.");
      const calibrationProblem = isLevelDevice ? calibrationError(state.calibration) : "";
      if (calibrationProblem) throw new Error(calibrationProblem);
      if (state.tankGeometry) {
        const checked = resolveTankSizing(state.tankSizing);
        if (!checked.geometry ||
            JSON.stringify(checked.geometry) !== JSON.stringify(state.tankGeometry)) {
          throw new Error("Tank size inputs changed or are invalid. Review and accept the helper result again.");
        }
      }

      // 1. customer
      if (!progress.customer) {
        if (state.branch.customerMode === "new") {
          const created = await api.createCustomer(buildCustomerPayload(state));
          progress.customer = created.entity_id;
        } else {
          progress.customer = entityIdOf(state.customer);
        }
        dispatch({ type: "MARK_PROGRESS", progress: { customer: progress.customer } });
      }

      // 2. address
      if (!progress.address) {
        if (state.branch.addressMode === "new" && state.addressForm) {
          const created = await api.createAddress(buildAddressPayload(state.addressForm, progress.customer));
          progress.address = created.address_id;
        } else {
          progress.address = addressIdOf(state.address);
        }
        dispatch({ type: "MARK_PROGRESS", progress: { address: progress.address } });
      }

      // 3. asset
      if (!progress.asset) {
        if (state.branch.assetMode === "new" && state.assetForm) {
          // An explicitly accepted helper result wins over the asset form for
          // geometry only - never for name, type, customer or address.
          const geometry = state.tankGeometry || resolveGeometry({
            shape: state.assetForm.shape,
            mode: state.assetForm.capacity_mode,
            dims: state.assetForm.dims,
            capacity_gallons: state.assetForm.capacity_gallons,
            max_fill_pct: state.assetForm.max_fill_pct,
            ratio: state.assetForm.ratio,
          });
          const attributeValues = {
            ...state.assetForm.attributes,
            ...tankAttributeMap(
              { ...geometry, shape: geometry.shape || state.assetForm.shape },
              null,
              Boolean(state.tankGeometry),
            ),
          };
          const created = await api.createAsset(
            buildAssetPayload({ ...state.assetForm, attributes: attributeValues }, progress.customer, progress.address),
          );
          progress.asset = created.address_item_id;
        } else {
          progress.asset = state.asset?.address_item_id;
        }
        dispatch({ type: "MARK_PROGRESS", progress: { asset: progress.asset } });
      }

      // 4. new contacts: saved before the access/alert plan needs their IDs;
      // a retry skips the ones already created.
      const contactFailures = [];
      for (let i = 0; i < state.contacts.length; i += 1) {
        const contact = state.contacts[i];
        const key = contactKey(contact, i);
        if (!contact.first_name?.trim() || progress.contacts[key]) continue;
        try {
          const created = await api.createContact(buildContactPayload(contact, progress.customer));
          progress.contacts[key] = created.contact_id || "created";
        } catch (e) {
          contactFailures.push(`${`${contact.first_name} ${contact.last_name}`.trim()}: ${installationMessage(e)}`);
        }
      }
      dispatch({ type: "MARK_PROGRESS", progress: { contacts: progress.contacts } });
      if (contactFailures.length) {
        const error = new Error(`Contact creation failed - ${contactFailures.join("; ")}`);
        error.assignment = true;
        throw error;
      }

      // 5. one core completion for the reviewed asset
      const billing = state.billingContactKey ? progress.contacts[state.billingContactKey] : null;
      const intents = {
        providerIds: state.providerSelection,
        contactAccess: accessRows
          .filter((row) => row.enabled)
          .map((row) => ({
            ...row,
            contact_id: row.contact_id || (typeof progress.contacts[row.key] === "number" ? progress.contacts[row.key] : null),
          })),
        alerts: {
          enabled: Boolean(state.alerts?.enabled),
          recipients: recipients.map((row) => ({
            ...row,
            contact_id: row.contact_id || (typeof progress.contacts[row.client_ref] === "number" ? progress.contacts[row.client_ref] : null),
          })),
        },
        // Reviewed four-field calibration. It becomes a claim-scoped
        // level_calibration operation only if the server supports it.
        levelCalibration: isLevelDevice ? calibrationPayload(state.calibration) : null,
      };
      // Exactly one calibration save path runs. The choice is made from the
      // target-scoped setup options, before anything is completed:
      // - server operation: calibration is frozen in the plan, applied and
      //   retried with the other follow-ups; any stale local request is cleared.
      // - interim direct path: write-ahead the request under its own storage
      //   key (independent of the draft cleared on success) so a storage
      //   failure blocks confirmation instead of silently dropping it.
      let pendingCalibration = null;
      const { completed, plan } = await completeReviewedInstallation({
        api,
        session,
        target: { entityId: progress.customer, addressId: progress.address, assetId: progress.asset },
        install: {
          deviceName: state.device.device_name,
          installationDate: state.device.installation_date,
          thresholds: submittedThresholdRows(state.thresholds),
          asset: { map_identifier: state.device.map_identifier.trim(), attributes: submittedAttributes(state) },
          billingContactId: typeof billing === "number" ? billing : null,
        },
        intents,
        beforeComplete: ({ calibrationViaOperation: viaOperation }) => {
          pendingCalibration = calibrationSave.prepare({
            claimId: session.claim_id,
            deviceId,
            assetId: progress.asset,
            value: isLevelDevice && !viaOperation ? state.calibration : { ...emptyCalibration(), enabled: false },
          });
        },
      });
      dispatch({ type: "MARK_PROGRESS", progress: { installation: "succeeded", claimId: session.claim_id, plan } });
      dispatch({ type: "SET_RESULT", result: completed });
      writeProgress(claimCode, { claimId: session.claim_id });
      clearDraft(claimCode);
      sessionStorage.removeItem(sessionKey(claimCode));
      setSubmitting(false);
      setOperations(plan.map((row) => ({ ...row, status: "pending" })));

      // 6. follow-ups - each reported individually, never repeated for successes
      await runFollowUps(session.claim_id, plan);

      // 7. interim path only (pendingCalibration is null when calibration went
      // through the server operation): calibration is saved through the
      // device endpoint after the claim and normal follow-ups. Its failures
      // stay in the calibration banner - never reported as a failed device
      // assignment, and never a reason to repeat claim completion.
      await calibrationSave.save(pendingCalibration);
    } catch (e) {
      const savedSomething = Boolean(progress.customer && state.branch.customerMode === "new") || Boolean(progress.address && state.branch.addressMode === "new") || Boolean(progress.asset && state.branch.assetMode === "new");
      if (savedSomething || e.assignment) {
        setAssignmentError(installationMessage(e));
      } else {
        setErrors([installationMessage(e)]);
      }
    } finally {
      submitLock.current = false;
      setSubmitting(false);
      setBusy(false);
    }
  }, [claimCode, runFollowUps, session, state, accessRows, recipients, isLevelDevice, deviceId, calibrationSave.prepare, calibrationSave.save]);

  // ---- render helpers ----
  const goToStep = (key) => {
    const index = steps.indexOf(key);
    if (index >= 0) dispatch({ type: "GO_TO", index });
  };

  const addressRow = useMemo(() => {
    const row = state.address?.attributes || state.address || {};
    return row.Address || row.address || row;
  }, [state.address]);

  const addressName = useMemo(() => {
    if (state.branch.addressMode === "new" && state.addressForm) {
      const f = state.addressForm;
      return [f.street_address1, f.street_address2, f.city, f.state, f.postal_code].filter(Boolean).join(", ");
    }
    return addressRow.formatted_address || [addressRow.street_address1, addressRow.street_address2, addressRow.city, addressRow.state, addressRow.postal_code].filter(Boolean).join(", ");
  }, [addressRow, state.addressForm, state.branch.addressMode]);

  const addressLabel = state.branch.addressMode === "new" ? state.addressForm?.name : addressRow.name;

  const assetName = useMemo(() => {
    if (state.branch.assetMode === "new" && state.assetForm) return state.assetForm.name;
    return state.asset?.name || state.asset?.map_identifier || state.asset?.item_name || "";
  }, [state.asset, state.assetForm, state.branch.assetMode]);

  const assetDetails = useMemo(() => {
    if (state.branch.assetMode === "new") return "";
    return (state.asset?.attributes || [])
      .filter((row) => String(row.value ?? "").trim())
      .map((row) => `${row?.Attribute?.label || row?.Attribute?.attribute_name || `Attribute ${row.attribute_id}`} ${row.value}`)
      .join("; ");
  }, [state.asset, state.branch.assetMode]);

  const billingContactName = useMemo(() => {
    if (!state.billingContactKey) return "";
    const index = state.contacts.findIndex((c, i) => contactKey(c, i) === state.billingContactKey);
    const contact = state.contacts[index];
    return contact ? `${contact.first_name} ${contact.last_name}`.trim() : "";
  }, [state.billingContactKey, state.contacts]);

  const providerNames = (state.providerSelection || []).map((id) => {
    const provider = state.providerOptions.find((p) => (p.provider_id ?? p.id) === id);
    return provider?.name || provider?.provider_name || `Provider #${id}`;
  });

  const renderStep = () => {
    switch (stepKey) {
      case STEPS.CUSTOMER_MODE:
        return <CustomerModeStep state={state} dispatch={dispatch} onNext={() => dispatch({ type: "NEXT" })} />;
      case STEPS.CUSTOMER_SEARCH:
        return (
          <CustomerSearchStep
            customers={customers}
            loading={customersLoading}
            selected={state.customer}
            profile={profile}
            onSelect={(customer) => dispatch({ type: "SELECT_CUSTOMER", customer })}
            t={t}
            darkMode={darkMode}
          />
        );
      case STEPS.CUSTOMER_NEW:
        return <CustomerCreateStep form={state.customerForm || emptyCustomerForm()} dropdowns={dropdowns} dispatch={dispatch} t={t} darkMode={darkMode} />;
      case STEPS.ADDRESS_MODE:
        return <AddressModeStep dispatch={dispatch} onNext={() => dispatch({ type: "NEXT" })} />;
      case STEPS.ADDRESS_SELECT:
        return (
          <AddressSelectStep
            addresses={profile?.addresses?.length ? profile.addresses : addresses}
            selected={state.address}
            onSelect={(address) => dispatch({ type: "SELECT_ADDRESS", address })}
            t={t}
          />
        );
      case STEPS.ADDRESS_NEW:
        return <AddressCreateStep form={state.addressForm || emptyAddressForm()} dropdowns={dropdowns} dispatch={dispatch} t={t} darkMode={darkMode} />;
      case STEPS.ASSET_MODE:
        return <AssetModeStep dispatch={dispatch} onNext={() => dispatch({ type: "NEXT" })} />;
      case STEPS.ASSET_SELECT:
        return (
          <AssetSelectStep
            assets={(targets || []).filter((a) => String(a.address_id) === String(addressIdOf(state.address)))}
            selected={state.asset}
            onSelect={(asset) => {
              dispatch({ type: "SELECT_ASSET", asset });
              if (!state.device.map_identifier && asset?.map_identifier) {
                dispatch({ type: "SET_DEVICE", device: { map_identifier: asset.map_identifier } });
              }
            }}
            t={t}
          />
        );
      case STEPS.ASSET_NEW:
        return (
          <AssetCreateStep
            form={state.assetForm || emptyAssetForm()}
            dropdowns={dropdowns}
            addressId={addressIdOf(state.address)}
            targets={targets}
            dispatch={dispatch}
            t={t}
            darkMode={darkMode}
          />
        );
      case STEPS.CONTACTS:
        return (
          <ContactsStep
            state={state}
            dispatch={dispatch}
            existingContacts={(profile?.recipients || []).filter((row) => row.scope === "entity")}
            accessRows={accessRows}
            dropdowns={dropdowns}
            t={t}
            darkMode={darkMode}
          />
        );
      case STEPS.DEVICE:
        return <DeviceDetailsStep state={state} overview={overview} dispatch={dispatch} t={t} darkMode={darkMode} />;
      case STEPS.CALIBRATION:
        return (
          <CalibrationStep
            value={state.calibration}
            dispatch={dispatch}
            t={t}
            darkMode={darkMode}
            tankSizing={state.tankSizing}
            tankGeometry={state.tankGeometry}
            deviceTest={deviceTestAvailable && session && deviceId ? {
              session,
              deviceId,
              socket,
              record: state.deviceTest,
              onContinue: () => dispatch({ type: "NEXT" }),
              canContinue: !gateError && !busy,
            } : null}
          />
        );
      case STEPS.THRESHOLDS:
        return <ThresholdsStep state={state} dispatch={dispatch} t={t} darkMode={darkMode} />;
      case STEPS.NOTIFICATIONS:
        return <NotificationsStep state={state} dispatch={dispatch} destinations={destinations} t={t} />;
      case STEPS.PROVIDERS:
        return <ProviderSharingStep state={state} dispatch={dispatch} t={t} />;
      case STEPS.REVIEW:
        return (
          <InstallationReviewStep
            state={state}
            summary={{
              customer: customerIdentity,
              addressName,
              addressLabel,
              assetName,
              assetDetails,
              billingContact: billingContactName,
              deviceId,
              accessRows,
              recipients,
              providerNames,
              calibrationViaOperation,
              deviceTestAvailable,
            }}
            blockers={reviewBlockers}
            termsVersion={termsVersion}
            onEdit={goToStep}
            dispatch={dispatch}
            t={t}
          />
        );
      default:
        return null;
    }
  };

  const renderActions = () => {
    if (stepKey === STEPS.REVIEW) {
      return (
        <>
          <SecondaryButton t={t} onClick={() => dispatch({ type: "BACK" })} disabled={busy}>
            Back
          </SecondaryButton>
          <PrimaryButton
            t={t}
            loading={busy}
            disabled={!state.attest.accuracy || !state.attest.terms || reviewBlockers.length > 0}
            onClick={submit}
          >
            {assignmentError ? "Retry device assignment" : "Confirm & install device"}
          </PrimaryButton>
        </>
      );
    }
    const canAdvance = !gateError;
    return (
      <>
        {state.stepIndex > 0 && (
          <SecondaryButton t={t} onClick={() => dispatch({ type: "BACK" })} disabled={busy}>
            Back
          </SecondaryButton>
        )}
        <PrimaryButton t={t} disabled={!canAdvance || busy} onClick={() => dispatch({ type: "NEXT" })}>
          Continue
        </PrimaryButton>
      </>
    );
  };

  const deviceContext = deviceId ? `Device #${deviceId}` : "";

  if (!auth.isAuthenticated) {
    const copy = claimStageCopy(CLAIM_STAGES.SIGN_IN);
    return (
      <WizardShell title={copy.heading} subtitle={`Sign in to continue this installation.${deviceContext ? ` ${deviceContext}.` : ""}`} t={t} darkMode={darkMode}>
        <ClaimHead stage={CLAIM_STAGES.SIGN_IN} />
        <PrimaryButton
          t={t}
          onClick={() => {
            sessionStorage.setItem("device_claim_return_to", `/onboard/claim/${claimCode}/install`);
            navigate("/auth/login");
          }}
        >
          Sign in to continue
        </PrimaryButton>
      </WizardShell>
    );
  }

  if (state.result || summary) {
    const stage = summary?.partial
      ? CLAIM_STAGES.HISTORY
      : outcomeStage({ completed: true, operations: operations || summary?.operations || [] }) || CLAIM_STAGES.FINISHING;
    const copy = claimStageCopy(summary ? stage : CLAIM_STAGES.FINISHING);
    // The server summary covers server-backed operations only. A pending or
    // failed calibration save is reported separately and honestly.
    const calibrationNeedsAttention = Boolean(
      calibrationSave.error ||
      (calibrationSave.job && calibrationSave.job.status !== "saved"),
    );
    const resultHeading = calibrationNeedsAttention
      ? "Device installed - calibration needs attention"
      : copy.heading;
    return (
      <WizardShell title={resultHeading} subtitle={deviceContext} t={t} darkMode={darkMode}>
        <ClaimHead stage={calibrationNeedsAttention
          ? (calibrationSave.error ? CLAIM_STAGES.SETUP_ATTENTION : CLAIM_STAGES.FINISHING)
          : summary ? stage : CLAIM_STAGES.FINISHING} />
        <ErrorList errors={errors} t={t} />
        {calibrationSave.job || calibrationSave.error ? (
          <Box mb={3}>
            <StepAlert severity={
              calibrationSave.error ? "error" :
                calibrationSave.job?.status === "saved" ? "success" : "warning"
            }>
              {calibrationSave.error ||
                (calibrationSave.job?.status === "saved"
                  ? "Calibration save confirmed in this browser. This is not a live configuration check."
                  : "Device installed. Calibration still needs to be saved.")}
            </StepAlert>
            {calibrationSave.job ? (
              <>
                <Box component="pre" sx={{ whiteSpace: "pre-wrap", fontSize: 12, color: t.textStrong }}>
                  {JSON.stringify(calibrationSave.job.payload, null, 2)}
                </Box>
                {calibrationSave.job.status === "pending" ? (
                  <>
                    <StepAlert severity="info">
                      Retry replaces this device's calibration with the measurements shown above.
                      If another technician may have changed it, verify the current configuration first.
                    </StepAlert>
                    <PrimaryButton t={t} loading={calibrationSave.saving}
                      disabled={busy || calibrationSave.saving}
                      onClick={() => calibrationSave.save(calibrationSave.job)}>
                      Retry calibration only
                    </PrimaryButton>
                  </>
                ) : null}
              </>
            ) : null}
          </Box>
        ) : null}
        {summary ? (
          <InstallationSummary
            summary={summary}
            operations={operations}
            onRetry={retryFollowUps}
            retrying={busy}
            error={followUpError}
          />
        ) : (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
      </WizardShell>
    );
  }

  if (!session) {
    let stage = CLAIM_STAGES.LOOKUP;
    if (routingError) stage = CLAIM_STAGES.ERROR;
    else if (routing && routing.available === false && !readSession(claimCode)) stage = CLAIM_STAGES.UNAVAILABLE;
    else if (routing) stage = CLAIM_STAGES.CUSTOMER;
    const reason = unavailableReason(routing?.unavailable_reason || routing?.reason || routing?.status);
    const copy = claimStageCopy(stage, { reason });
    return (
      <WizardShell title={copy.heading} subtitle={deviceContext} t={t} darkMode={darkMode}>
        <ClaimHead stage={stage} reason={reason} />
        <ErrorList errors={errors} t={t} />
        {busy && !routing ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <ClaimGate
            routing={routing}
            overview={overview}
            hasStoredSession={Boolean(readSession(claimCode))}
            claimCode={claimCode}
            busy={busy}
            onSignIn={() => {
              sessionStorage.setItem("device_claim_return_to", `/onboard/claim/${claimCode}/install`);
              navigate("/auth/login");
            }}
            onStart={startOrResume}
          />
        )}
      </WizardShell>
    );
  }

  const stage = submitting
    ? CLAIM_STAGES.SUBMITTING
    : assignmentError && stepKey === STEPS.REVIEW
      ? CLAIM_STAGES.ASSIGNMENT_ATTENTION
      : STEP_STAGE[stepKey] || CLAIM_STAGES.CUSTOMER;
  const copy = claimStageCopy(stage);
  const sectionIndex = SECTIONS.findIndex((section) => section.key === STEP_SECTION[stepKey]);

  return (
    <WizardShell
      title={copy.heading}
      subtitle={[
        sectionIndex >= 0 ? `Step ${sectionIndex + 1} of ${SECTIONS.length}: ${meta.title}` : meta.title,
        deviceContext,
      ].filter(Boolean).join(" · ")}
      onBack={state.stepIndex > 0 ? () => dispatch({ type: "BACK" }) : undefined}
      t={t}
      darkMode={darkMode}
      actions={renderActions()}
    >
      <ClaimHead stage={stage} />
      <ErrorList errors={errors} t={t} />
      {assignmentError ? (
        <StepAlert severity="error">
          {state.progress.customer && state.branch.customerMode === "new"
            ? "Customer saved - device assignment needs attention. "
            : "Device assignment needs attention. "}
          {assignmentError} Saved records are kept; retrying will not create them again.
        </StepAlert>
      ) : null}
      {gateError && stepKey !== STEPS.REVIEW ? <StepAlert severity="info">{gateError}</StepAlert> : null}
      {!routing?.available ? (
        <StepAlert severity="warning">This claim is unavailable. You can still resume a saved session.</StepAlert>
      ) : null}
      {renderStep()}
    </WizardShell>
  );
}
